import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';

import { OnboardingStyle } from '../public/onboarding/onboarding-style';
import usePushDataLayerEvent from '../../utils/google-analytics.util';
import { logData } from '../../utils/debug.util';
import { hideModal, showModal } from '../../store/slices/generalSlice';
import { RootState } from '../../store/rootReducer';
import useManageStorage from '../../hooks/useManageStorage/useManageStorage';
import { AuthHeader, ContentLayout } from '../../global-styles/layout';
import { ASITUR_CODE, INSURANCE_COMPANIES } from '../../constants';
import { Heading2Text } from '../../components/typography/typography';
import ModalContainer from '../../components/common/modal-container/modal-container';
import { findSurvey } from '../../api/survey/survey';
import { CompletedParams } from './completed.types';
import { CompletedStyles } from './completed.style';

const Completed = () => {
  const { t } = useTranslation(['common', 'completed']);
  const { file, intervention } = useParams<CompletedParams>();

  const [surveyExists, setSurveyExists] = useState(true);
  const [postponedSurvey, setPostponedSurvey] = useState(false);
  const modal = useSelector((state: RootState) => state.general.modal);
  const provider = useSelector((state: RootState) => state.general.provider);

  const { pushDataLayerEvent } = usePushDataLayerEvent();
  const { writeToStorage } = useManageStorage();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!surveyExists) {
      dispatch(showModal({ name: 'completed-quiz' }));
    } else {
      dispatch(hideModal());
    }
  }, [dispatch, surveyExists]);

  useEffect(() => {
    const checkSurvey = async () => {
      if (intervention) {
        const survey = await findSurvey(intervention);
        setSurveyExists(survey.codigo === '1');
      }
    };
    checkSurvey();
  }, [file, intervention]);

  useEffect(() => {
    if (postponedSurvey) {
      const interval = setInterval(() => {
        dispatch(showModal({ name: 'completed-quiz' }));
        setPostponedSurvey(false);
      }, 60000 * 10);

      return () => clearInterval(interval);
    }
  }, [dispatch, postponedSurvey]);

  const handleDoSurvey = async () => {
    if (!file) return;
    pushDataLayerEvent('empezar_encuesta', null, 'finalizar-servicio');
    await writeToStorage('surveyFromCompleted', true);
    navigate(`/survey/${file}/${intervention}`);
  };

  const handlePostponeSurvey = () => {
    pushDataLayerEvent('posponer_encuesta', null, 'finalizar-servicio');
    dispatch(hideModal());
    setPostponedSurvey(true);
  };

  const handleViewAssistances = () => {
    navigate(`/completed/${file}/${intervention}/assistances`);
  };

  const handleViewCoverages = () => {
    navigate(`/completed/${file}/${intervention}/coverages`);
  };

  const handleNewAssistance = () => {
    const newAssistanceUri = `/${INSURANCE_COMPANIES[provider || ASITUR_CODE].webParam}`;
    logData('Navigating to new assistance URI:', newAssistanceUri);
    navigate(newAssistanceUri);
  };

  return (
    <ContentLayout>
      <CompletedStyles>
        <OnboardingStyle>
          <div className="onboarding-top">
            <AuthHeader>
              <div className="auth-header-title">
                <Heading2Text as="h1" weight={600}>
                  {t('completed:title')}
                </Heading2Text>
              </div>
            </AuthHeader>
            <div className="vas-buttons">
              <Button component="a" className="vas-button" onClick={handleViewAssistances}>
                {t('completed:history')}
              </Button>
              <Button component="a" className="vas-button" onClick={handleNewAssistance}>
                {t('completed:new-assistance')}
              </Button>
              <Button component="a" className="vas-button" onClick={handleViewCoverages}>
                {t('completed:policy-details')}
              </Button>
              <Button component="a" className="vas-button" onClick={() => navigate(`/completed/done`)}>
                {t('completed:terminate')}
              </Button>
            </div>
          </div>
        </OnboardingStyle>
      </CompletedStyles>

      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: handleDoSurvey,
            label: t('aia:confirmation.success.do-survey'),
          },
          {
            className: 'secondary',
            onClick: handlePostponeSurvey,
            label: t('aia:confirmation.success.to-do-later'),
          },
        ]}
        active={modal.name === 'completed-quiz'}
        onClose={() => dispatch(hideModal())}
        title={t('aia:confirmation.success.quiz-modal.title')}
        text={t('aia:confirmation.success.quiz-modal.text')}
        dataCy="reset-form-modal"
      ></ModalContainer>
    </ContentLayout>
  );
};

export default Completed;
